import React from 'react';
import {
  Box,
  Button,
  ButtonText,
  HStack,
  Icon,
  Input,
  InputField,
  Pressable,
  ScrollView,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { WandSparkles } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useReplaceOralExamPool,
  useUpdateOralExamConfig,
} from '@/common/api/endpoints/oral-exam/oral-exam';
import { getGetTasksByModuleVersionQueryKey } from '@/common/api/endpoints/tasks/tasks';
import type { OralExamConfig } from '@/common/api/models/oralExamConfig';
import type { OralExamQuestion } from '@/common/api/models/oralExamQuestion';
import ContentGroup from '@/common/components/ContentGroup';
import { hasFillSlot, sanitizeFillsForSave } from '@/common/components/oralExamPreviewQuestion';
import { useDialog } from '@/common/context/DialogContext';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { useOralExamPoolJob } from '@/lecturer/ModuleEditor/hooks/useOralExamPoolJob';
import { EditableQuestionPool } from './EditableQuestionPool';

// mb is intentionally omitted here; callers add vertical spacing via parent VStack/Box
const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text size="xs" fontWeight="bold" color="$textSubtle" textTransform="uppercase" letterSpacing="$md">
    {children}
  </Text>
);

const DEFAULT_GENERATE_QUESTION_COUNT = 20;
const MIN_QUESTION_COUNT = 1;
const MAX_QUESTION_COUNT = 50;

interface OralExamConfigFormProps {
  moduleId: string;
  version: string;
  taskId: string;
  oralExamConfig?: OralExamConfig | null;
  isReadOnly?: boolean;
}

const parseOptionalPositiveInt = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? Number.NaN : parsed;
};

export const OralExamConfigForm: React.FC<OralExamConfigFormProps> = ({
  moduleId,
  version,
  taskId,
  oralExamConfig,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const { showToastMessage, showDialog } = useDialog();
  const queryClient = useQueryClient();
  const { tasksQueryKey: liveTasksQueryKey, tasks } = useLecturerModuleData(moduleId, version, taskId);
  const tasksQueryKey = liveTasksQueryKey ?? getGetTasksByModuleVersionQueryKey(moduleId, version, { include: 'resources' });
  const { launch: launchPoolJob, isJobActive, isLaunching } = useOralExamPoolJob({
    moduleId,
    version,
    taskId,
  });

  const { taskMetaById, resourceTitleById } = React.useMemo(() => {
    const taskMeta: Record<string, { title: string; level: number }> = {};
    const resourceTitles: Record<string, string> = {};
    for (const task of tasks ?? []) {
      if (task.id) {
        taskMeta[task.id] = { title: task.title, level: task.level };
      }
      for (const resource of task.resources ?? []) {
        if (resource.id) {
          resourceTitles[resource.id] = resource.title;
        }
      }
    }
    return { taskMetaById: taskMeta, resourceTitleById: resourceTitles };
  }, [tasks]);
  const updateConfigMutation = useUpdateOralExamConfig();
  const replacePoolMutation = useReplaceOralExamPool();

  // Prefer the live tasks query over the parent prop so a completed background
  // job refreshes the list even if TaskEditor has not re-seeded yet.
  const liveConfig = React.useMemo(() => {
    const liveTask = (tasks ?? []).find((tk) => tk.id === taskId);
    return liveTask?.oralExamConfig ?? oralExamConfig;
  }, [oralExamConfig, taskId, tasks]);

  const serverPool = liveConfig?.questionPool ?? [];
  const isSaving = replacePoolMutation.isPending;

  // Local pool: null = display server pool (clean); non-null = pending edits (dirty)
  const [localPool, setLocalPool] = React.useState<OralExamQuestion[] | null>(null);
  const isDirty = localPool !== null;

  const isGenerating = isJobActive || isLaunching;
  // Clear the visible pool while generating so the user sees old questions
  // being replaced rather than lingering until the new pool arrives.
  const displayPool = isGenerating ? [] : (localPool ?? serverPool);

  // Reset local state when taskId changes (editor switched to different task)
  React.useEffect(() => {
    setLocalPool(null);
  }, [taskId]);

  const [questionCountInput, setQuestionCountInput] = React.useState(
    () => (oralExamConfig?.questionCount != null ? String(oralExamConfig.questionCount) : ''),
  );
  const [secondsPerQuestionInput, setSecondsPerQuestionInput] = React.useState(
    () => (oralExamConfig?.secondsPerQuestion != null ? String(oralExamConfig.secondsPerQuestion) : ''),
  );
  // Pool size is a generation-only input (how many questions to generate); it is
  // NOT a persisted override. Empty means the default of 20, not the current pool
  // length — otherwise a short previous run locks every regenerate at that size.
  const [poolSizeInput, setPoolSizeInput] = React.useState('');

  React.useEffect(() => {
    setQuestionCountInput(liveConfig?.questionCount != null ? String(liveConfig.questionCount) : '');
    setSecondsPerQuestionInput(
      liveConfig?.secondsPerQuestion != null ? String(liveConfig.secondsPerQuestion) : '',
    );
  }, [liveConfig?.questionCount, liveConfig?.secondsPerQuestion, taskId]);

  // Overrides are opt-in and hidden by default (they should stay at the inherited
  // defaults ~90% of the time). Pre-open when the loaded task already carries a
  // persisted override, mirroring the "additional comments" toggle in TaskBaseForm.
  const hasPersistedOverride = liveConfig?.questionCount != null
    || liveConfig?.secondsPerQuestion != null;
  const [showOverrides, setShowOverrides] = React.useState(() => hasPersistedOverride);
  React.useEffect(() => {
    setShowOverrides(hasPersistedOverride);
  }, [taskId, hasPersistedOverride]);

  const invalidateTaskQueries = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: tasksQueryKey });
  }, [queryClient, tasksQueryKey]);

  const saveOverride = React.useCallback((data: {
    questionCount?: number | null;
    secondsPerQuestion?: number | null;
  }) => {
    updateConfigMutation.mutate(
      {
        moduleId, version, taskId, data,
      },
      {
        onSuccess: async () => { await invalidateTaskQueries(); },
        onError: () => { showToastMessage('error', t('lecturer.oralExamConfig.messages.configFailed')); },
      },
    );
  }, [invalidateTaskQueries, moduleId, showToastMessage, t, taskId, updateConfigMutation, version]);

  const handleQuestionCountBlur = () => {
    const parsed = parseOptionalPositiveInt(questionCountInput);
    if (Number.isNaN(parsed)) { showToastMessage('error', t('lecturer.oralExamConfig.errors.mustBeNumber')); return; }
    if (parsed != null && (parsed < MIN_QUESTION_COUNT || parsed > MAX_QUESTION_COUNT)) {
      showToastMessage('error', t('lecturer.oralExamConfig.errors.questionCountRange')); return;
    }
    if (parsed === (liveConfig?.questionCount ?? null)) return;
    saveOverride({ questionCount: parsed });
  };

  const handleSecondsPerQuestionBlur = () => {
    const parsed = parseOptionalPositiveInt(secondsPerQuestionInput);
    if (Number.isNaN(parsed)) { showToastMessage('error', t('lecturer.oralExamConfig.errors.mustBeNumber')); return; }
    if (parsed != null && parsed < 1) {
      showToastMessage('error', t('lecturer.oralExamConfig.errors.secondsPerQuestionPositive')); return;
    }
    if (parsed === (liveConfig?.secondsPerQuestion ?? null)) return;
    saveOverride({ secondsPerQuestion: parsed });
  };

  const handleToggleOverrides = (enabled: boolean) => {
    setShowOverrides(enabled);
    if (!enabled) {
      // Turning off reverts to inherited defaults: clear persisted overrides and
      // reset the local inputs (pool size is local-only).
      setQuestionCountInput('');
      setSecondsPerQuestionInput('');
      setPoolSizeInput('');
      if (hasPersistedOverride) {
        saveOverride({ questionCount: null, secondsPerQuestion: null });
      }
    }
  };

  const doGenerate = React.useCallback(async () => {
    const parsedPoolSize = parseOptionalPositiveInt(poolSizeInput);
    const validPoolSize = parsedPoolSize && !Number.isNaN(parsedPoolSize) ? parsedPoolSize : null;
    const questionCount = validPoolSize ?? DEFAULT_GENERATE_QUESTION_COUNT;
    // Regenerate is a decisive action: the confirmation dialog already gated
    // discarding pending local edits, and the new pool is persisted by the job.
    // Save/Discard remain only for per-item edits.
    setLocalPool(null);
    await launchPoolJob({
      questionCount,
      label: t('lecturer.oralExamConfig.generateJobLabel'),
    });
  }, [launchPoolJob, poolSizeInput, t]);

  const handleGenerate = () => {
    if (isDirty) {
      showDialog({
        title: t('lecturer.oralExamConfig.regenerateWithDirtyTitle'),
        message: t('lecturer.oralExamConfig.regenerateWithDirtyMessage'),
        buttons: [
          { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
          { text: t('lecturer.oralExamConfig.regenerateConfirm'), action: 'negative', onPress: doGenerate },
        ],
      });
    } else {
      doGenerate();
    }
  };

  const handleSavePool = () => {
    if (!localPool) return;
    const sanitizedPool = localPool.map((question) => ({
      ...question,
      fills: hasFillSlot(question.question)
        ? sanitizeFillsForSave((question as OralExamQuestion & { fills?: string[] }).fills)
        : [],
    }));
    replacePoolMutation.mutate(
      {
        moduleId,
        version,
        taskId,
        data: { questionPool: sanitizedPool },
      },
      {
        onSuccess: async () => {
          setLocalPool(null);
          await invalidateTaskQueries();
          showToastMessage('success', t('lecturer.oralExamConfig.messages.poolSaved'));
        },
        onError: () => { showToastMessage('error', t('lecturer.oralExamConfig.messages.poolSaveFailed')); },
      },
    );
  };

  const handleDiscardPool = () => setLocalPool(null);

  const showGenerateButton = !isReadOnly;

  return (
    <Box flex={1} bg="$bgApp">
      <ScrollView flex={1} bg="$bgApp">
        <VStack space="md" px="$4" py="$3">
          <VStack space="sm">
            <HStack alignItems="center" space="sm">
              <Switch
                size="sm"
                isDisabled={isReadOnly}
                value={showOverrides}
                onToggle={handleToggleOverrides}
                testID="oral-exam-customize-toggle"
              />
              <Text size="xs" color="$textBody">
                {t('lecturer.oralExamConfig.customizeToggleLabel')}
              </Text>
            </HStack>
            {showOverrides && (
            <ContentGroup variant="bordered" p="$3" accentWidth={4}>
              <HStack flexWrap="wrap" space="xl" alignItems="center">
                <HStack alignItems="center" space="sm">
                  <Text size="xs" color="$textSubtle">
                    {t('lecturer.oralExamConfig.poolSizeLabel')}
                  </Text>
                  <Input isDisabled={isReadOnly} size="sm" w={80}>
                    <InputField
                      value={poolSizeInput}
                      onChangeText={setPoolSizeInput}
                      placeholder={t('lecturer.oralExamConfig.poolSizePlaceholder')}
                      keyboardType="numeric"
                      size="sm"
                      editable={!isReadOnly}
                      testID="oral-exam-pool-size-input"
                    />
                  </Input>
                </HStack>

                <HStack alignItems="center" space="sm">
                  <Text size="xs" color="$textSubtle">
                    {t('lecturer.oralExamConfig.questionCountLabel')}
                  </Text>
                  <Input isDisabled={isReadOnly || updateConfigMutation.isPending} size="sm" w={80}>
                    <InputField
                      value={questionCountInput}
                      onChangeText={setQuestionCountInput}
                      onBlur={handleQuestionCountBlur}
                      placeholder={t('lecturer.oralExamConfig.questionCountPlaceholder')}
                      keyboardType="numeric"
                      size="sm"
                      editable={!isReadOnly}
                      testID="oral-exam-question-count-input"
                    />
                  </Input>
                </HStack>

                <HStack alignItems="center" space="sm">
                  <Text size="xs" color="$textSubtle">
                    {t('lecturer.oralExamConfig.secondsPerQuestionLabel')}
                  </Text>
                  <Input isDisabled={isReadOnly || updateConfigMutation.isPending} size="sm" w={80}>
                    <InputField
                      value={secondsPerQuestionInput}
                      onChangeText={setSecondsPerQuestionInput}
                      onBlur={handleSecondsPerQuestionBlur}
                      placeholder={t('lecturer.oralExamConfig.secondsPerQuestionPlaceholder')}
                      keyboardType="numeric"
                      size="sm"
                      editable={!isReadOnly}
                      testID="oral-exam-seconds-per-question-input"
                    />
                  </Input>
                </HStack>
              </HStack>
            </ContentGroup>
            )}
          </VStack>

          <Box>
            <HStack alignItems="center" justifyContent="space-between" mb="$2">
              <HStack alignItems="center" space="sm" flex={1}>
                <SectionHeader>{t('lecturer.oralExamConfig.poolTitle')}</SectionHeader>
                {isGenerating && (
                  <HStack space="xs" alignItems="center">
                    <Spinner size="small" style={{ transform: [{ scale: 0.6 }] }} />
                    <Text size="xs" color="$textSubtle">
                      {t('lecturer.oralExamConfig.generating')}
                    </Text>
                  </HStack>
                )}
                {showGenerateButton && !isGenerating && (
                  <Pressable onPress={handleGenerate} testID="oral-exam-generate-button">
                    <HStack space="xs" alignItems="center">
                      <Icon as={WandSparkles} size="xs" color="$brandBase" />
                      <Text size="xs" color="$brandBase">
                        {displayPool.length > 0
                          ? t('lecturer.oralExamConfig.regenerateButton')
                          : t('lecturer.oralExamConfig.generateButton')}
                      </Text>
                    </HStack>
                  </Pressable>
                )}
              </HStack>

              {isDirty && !isReadOnly && (
                <HStack space="sm" alignItems="center">
                  <Button
                    size="xs"
                    variant="outline"
                    action="secondary"
                    onPress={handleDiscardPool}
                    isDisabled={isSaving}
                    testID="oral-exam-discard-button"
                  >
                    <ButtonText>{t('lecturer.oralExamConfig.discardPool')}</ButtonText>
                  </Button>
                  <Button
                    size="xs"
                    action="primary"
                    onPress={handleSavePool}
                    isDisabled={isSaving}
                    testID="oral-exam-save-pool-button"
                  >
                    {isSaving ? <Spinner size="small" /> : (
                      <ButtonText>{t('lecturer.oralExamConfig.savePool')}</ButtonText>
                    )}
                  </Button>
                </HStack>
              )}
            </HStack>

            <EditableQuestionPool
              questions={displayPool}
              onChange={isReadOnly ? () => undefined : setLocalPool}
              generatedAt={liveConfig?.generatedAt ?? null}
              lastEditedAt={liveConfig?.lastEditedAt ?? null}
              isReadOnly={isReadOnly}
              isGenerating={isGenerating}
              taskMetaById={taskMetaById}
              resourceTitleById={resourceTitleById}
            />
          </Box>
        </VStack>
      </ScrollView>
    </Box>
  );
};

export default OralExamConfigForm;
